import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useSessionStorage } from '@monofront/business/hooks';
import { useMutation } from '@apollo/client';
import { VALIDATE_SUBSCRIPTION_PROMOTION_CODE } from '@monofront/business/queries';
import { getDiscountPrice } from '@monofront/business/utils';
import logger from '@utils/logger';
import { IPromotionCode } from '@monofront/types';
import { useAuthentication } from '@context/businessContainer';
import useSubscriptionPrice from '@hooks/useSubscriptionPrice';
import { DEFAULT_ANNUAL_SUBSCRIPTION_PRICE } from '@monofront/business/constants';
import { SESSIONSTORAGE_COUPON } from '@constants/sessionStorage';
import { useDiscountCode } from '@hooks/useDiscountCode';

interface ICallbacks {
  onSuccess?: (coupon) => unknown;
  onError?: (err) => unknown;
}

interface IValidatedCoupon extends IPromotionCode {
  price: number;
}

interface IMembershipCouponContext {
  error: string | null;
  loading: boolean;
  isCouponValid: boolean;
  applyCoupon: (
    coupon: string,
    { onSuccess, onError }: ICallbacks,
  ) => Promise<IPromotionCode | null | undefined>;
  validatedCoupon: IValidatedCoupon;
  discountedPrice: number;
  removeCoupon: () => void;
}

const defaultSetters = {
  applyCoupon: (async () =>
    undefined) as IMembershipCouponContext['applyCoupon'],
  removeCoupon: (() => undefined) as IMembershipCouponContext['removeCoupon'],
};

const defaultCouponCode: IValidatedCoupon = {
  id: '',
  code: '',
  amountOff: 0,
  percentOff: 0,
  price: DEFAULT_ANNUAL_SUBSCRIPTION_PRICE,
};

const defaultValues = {
  error: null,
  loading: false,
  isCouponValid: false,
  validatedCoupon: defaultCouponCode,
  discountedPrice: 0,
};

const MembershipCouponContext = createContext<IMembershipCouponContext>({
  ...defaultValues,
  ...defaultSetters,
});

/**
 * Context who checks validity and provides verified membership promotion code
 * @returns MembershipCouponContext, MembershipCouponProvider
 */
const MembershipCouponProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { annualSubscriptionPrice } = useSubscriptionPrice();

  const defaultCouponValue = {
    ...defaultCouponCode,
    price: annualSubscriptionPrice,
  };

  const t = useTranslations('Account');
  const { isAuthenticated } = useAuthentication();

  const [discountCode] = useDiscountCode();
  const [coupon, setCoupon] = useSessionStorage(
    SESSIONSTORAGE_COUPON,
    defaultCouponValue,
  );

  useEffect(() => {
    // prevent desync between local storage and channel based subscription's price
    setCoupon({
      ...coupon,
      price: getDiscountPrice(annualSubscriptionPrice, coupon),
    });
  }, [annualSubscriptionPrice]);

  const [validatePromotionCode, { loading, reset }] = useMutation<{
    validateSubscriptionPromotionCode: IPromotionCode;
  }>(VALIDATE_SUBSCRIPTION_PROMOTION_CODE);

  const [isCouponValid, setIsCouponValid] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleRemoveCoupon = () => {
    reset();
    setCoupon(defaultCouponValue);
    setIsCouponValid(false);
  };

  const applyCoupon = async (
    coupon: string,
    { onSuccess, onError }: ICallbacks = {},
  ) => {
    if (coupon.trim().length > 0) {
      try {
        const result = await validatePromotionCode({
          variables: { promotionCode: coupon },
        });

        if (result.data) {
          const coupon = result.data.validateSubscriptionPromotionCode;

          setCoupon({
            id: coupon.id,
            code: coupon.code,
            price: getDiscountPrice(annualSubscriptionPrice, coupon),
            amountOff: coupon.amountOff,
            percentOff: coupon.percentOff,
          });

          setError(null);
          setIsCouponValid(true);

          if (onSuccess) {
            onSuccess(result.data.validateSubscriptionPromotionCode);
          }

          return coupon;
        }

        return null;
      } catch (e: any) {
        const [firstError] = e?.graphQLErrors || [];
        const errorMessage =
          firstError?.code === 'PROMOTION_CODE_NOT_ALLOWED'
            ? t('ineligibleToPromotionCode')
            : t('promotionCodeNotFound');

        logger.error('Error occured when validating promotion code', {
          exception: e,
        });

        setError(errorMessage);
        setIsCouponValid(false);
        handleRemoveCoupon();

        if (onError) {
          onError(e);
        }
      }
    }
  };

  useEffect(() => {
    // On mount, if a coupon is present in storage, we check for it's validity
    const existingCoupon = coupon?.code || discountCode;

    if (isAuthenticated && existingCoupon) {
      applyCoupon(existingCoupon);
    }
  }, [discountCode, isAuthenticated]);

  return (
    <MembershipCouponContext.Provider
      value={{
        error,
        loading,
        isCouponValid,
        applyCoupon,
        validatedCoupon: coupon,
        discountedPrice: isCouponValid
          ? getDiscountPrice(annualSubscriptionPrice, coupon)
          : annualSubscriptionPrice,
        removeCoupon: handleRemoveCoupon,
      }}
    >
      {children}
    </MembershipCouponContext.Provider>
  );
};

const useMembershipCouponContext = () => useContext(MembershipCouponContext);

export {
  MembershipCouponContext,
  MembershipCouponProvider,
  useMembershipCouponContext,
};
